import { useEffect, useRef, useState, type RefObject } from "react";

const BAR_WIDTH = 3;
const BAR_STEP = 5;
const PATTERN = [0.35, 0.6, 0.9, 0.5, 0.8, 0.4, 0.7, 0.3, 0.65];

/**
 * Bar heights (0..1) for `count` bars: the loudest sample of each slice of
 * the decoded peaks, scaled so the loudest bar fills the height; a steady
 * pattern when there are no peaks. Pure, tested.
 */
export function waveformBars(peaks: number[] | null, count: number): number[] {
  if (!peaks || peaks.length === 0) return Array.from({ length: count }, (_, i) => PATTERN[i % PATTERN.length]);
  const slice = peaks.length / count;
  const raw = Array.from({ length: count }, (_, i) => {
    let max = 0;
    for (let j = Math.floor(i * slice); j < Math.max(Math.floor((i + 1) * slice), Math.floor(i * slice) + 1); j++) {
      max = Math.max(max, Math.abs(peaks[j] ?? 0));
    }
    return max;
  });
  const top = Math.max(...raw, 0.0001);
  return raw.map((value) => Math.max(0.08, value / top));
}

/**
 * Draws the waveform of `audioUrl` on a canvas, with the played part and a
 * playhead following `audioRef`; a click on the canvas seeks. Peaks are
 * decoded once per URL (600 samples). Moved as is from the validate-audio
 * session so every listening task draws the same waveform.
 *
 * ```ts
 * const { canvasRef } = useAudioWaveform({ audioRef, audioUrl: current?.audioUrl, theme });
 * <TaskAudioPlayer canvasRef={canvasRef} ... />
 * ```
 */
export function useAudioWaveform({
  audioRef,
  audioUrl,
  theme,
}: {
  audioRef: RefObject<HTMLAudioElement>;
  audioUrl?: string | null;
  theme?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [peaks, setPeaks] = useState<number[] | null>(null);

  useEffect(() => {
    const loadPeaks = async () => {
      if (!audioUrl) { setPeaks(null); return; }
      try {
        const res = await fetch(audioUrl);
        const arrayBuf = await res.arrayBuffer();
        const AC = (window as unknown as Record<string, typeof AudioContext>).AudioContext
          || (window as unknown as Record<string, typeof AudioContext>).webkitAudioContext;
        const ctx = new AC();
        const audioBuf: AudioBuffer = await new Promise((resolve, reject) => {
          ctx.decodeAudioData(arrayBuf.slice(0), resolve, reject);
        });
        const channel = audioBuf.getChannelData(0);
        const samples = 600;
        const block = Math.max(1, Math.floor(channel.length / samples));
        const next: number[] = new Array(samples);
        for (let i = 0; i < samples; i++) {
          let max = 0;
          const start = i * block;
          for (let j = 0; j < block; j++) {
            const v = Math.abs(channel[start + j] || 0);
            if (v > max) max = v;
          }
          next[i] = max;
        }
        setPeaks(next);
        ctx.close?.();
      } catch {
        setPeaks(null);
      }
    };
    loadPeaks();
  }, [audioUrl]);

  useEffect(() => {
    // Bars like TogoAiLab: real peaks when the file could be decoded, a steady
    // pattern otherwise (private URL, CORS, codec), so the player never shows
    // an empty box. The played part takes the brand colour.
    const css = (name: string, alpha: number) => {
      const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      return value ? `hsl(${value} / ${alpha})` : `rgba(148, 163, 184, ${alpha})`;
    };
    const draw = (progress = 0) => {
      if (!canvasRef.current) return;
      const canvas = canvasRef.current;
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth || 0;
      const height = 40;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.floor(height * dpr);
      const ctx = canvas.getContext("2d");
      if (!ctx || width === 0) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const bars = waveformBars(peaks, Math.max(8, Math.floor(width / BAR_STEP)));
      const played = css("--primary", 1);
      const idle = css("--foreground", theme === "dark" ? 0.22 : 0.18);
      const cutoff = progress * bars.length;
      bars.forEach((amplitude, i) => {
        const barHeight = Math.max(4, amplitude * (height - 6));
        const x = i * BAR_STEP;
        const y = (height - barHeight) / 2;
        ctx.fillStyle = i < cutoff ? played : idle;
        ctx.beginPath();
        if (typeof ctx.roundRect === "function") ctx.roundRect(x, y, BAR_WIDTH, barHeight, BAR_WIDTH / 2);
        else ctx.rect(x, y, BAR_WIDTH, barHeight);
        ctx.fill();
      });
    };

    let raf = 0;
    const onResize = () => draw();
    const loop = () => {
      const a = audioRef.current;
      const dur = a?.duration || 0;
      const cur = a?.currentTime || 0;
      const p = dur > 0 ? cur / dur : 0;
      draw(p);
      raf = requestAnimationFrame(loop);
    };
    const onClick = (e: MouseEvent) => {
      const canvas = canvasRef.current;
      if (!canvas || !audioRef.current) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const w = canvas.clientWidth || rect.width;
      const ratio = Math.min(1, Math.max(0, x / Math.max(1, w)));
      const a = audioRef.current;
      if (a.duration > 0) {
        a.currentTime = ratio * a.duration;
        draw(ratio);
      }
    };

    const canvas = canvasRef.current;
    window.addEventListener("resize", onResize);
    canvas?.addEventListener("click", onClick);
    loop();
    return () => {
      window.removeEventListener("resize", onResize);
      canvas?.removeEventListener("click", onClick);
      cancelAnimationFrame(raf);
    };
  }, [peaks, theme, audioRef]);

  return { canvasRef, peaks };
}
